import { useEffect, useMemo, useState } from "preact/hooks";

import { fetchHealthEvents } from "../lib/api";
import { strongestLevel, type HealthNoteLevel } from "../lib/healthNotes";
import type { HealthEventOut } from "../types";

const DAY_MS = 24 * 60 * 60 * 1000;

/** design-desktop-phase-a.md §2.3 — "Health note" badge window, shared by
 * every surface that shows the health-note badge (Dashboard's Animals
 * snapshot card, and — sprint-011 A8 — the full Animals-page grid). */
export const HEALTH_NOTE_WINDOW_DAYS = 14;

export interface AnimalHealthNotes {
  eventsByAnimal: Record<string, HealthEventOut[]>;
  loading: boolean;
  /** `true` when the given animal has a health event dated within
   * `HEALTH_NOTE_WINDOW_DAYS` — the exact signal `.has-health-note`/
   * "Health note" badge renders on. */
  hasRecentNote: (animalId: string) => boolean;
  /** The badge level for this animal's events inside the same window:
   * `"attention"` (clay "Health note"), `"routine"` (neutral "Routine
   * care"), or `null` (no recent event). See `lib/healthNotes.ts`. */
  recentNoteLevel: (animalId: string) => HealthNoteLevel | null;
}

/**
 * §2.3's "Animals fan-out" — one `GET /animals/{id}/health-events` call
 * per animal id, in parallel (bounded, small N, runs once per `idsKey`
 * change — flagged N+1, accepted for this app's scale per §2.3's own
 * note and §7 item 3). Originally `DashboardView.tsx`'s own local
 * `useAnimalHealthFanOut`; sprint-011 A8 lifted it out here, unchanged,
 * so the full Animals-page grid (`AnimalsView.tsx`) can badge the SAME
 * "recent health note" signal the Dashboard mini-grid already shows,
 * rather than re-deriving (or silently drifting from) it. `idsKey` (not
 * the `animalIds` array reference, which changes identity every
 * re-render) is the effect dependency, so this only refetches when the
 * actual SET of ids changes.
 */
export function useAnimalHealthNotes(animalIds: string[]): AnimalHealthNotes {
  const idsKey = animalIds.join(",");
  const [resolved, setResolved] = useState<{
    key: string;
    events: Record<string, HealthEventOut[]>;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (animalIds.length === 0) {
      setResolved({ key: idsKey, events: {} });
      return undefined;
    }
    (async () => {
      const pairs = await Promise.all(
        animalIds.map(async (id) => {
          try {
            return [id, await fetchHealthEvents(id)] as const;
          } catch {
            // A single animal's history failing to load never blocks the
            // rest of the surface — same "degrade gracefully, per card"
            // posture every other Dashboard card takes.
            return [id, []] as const;
          }
        }),
      );
      if (cancelled) return;
      setResolved({ key: idsKey, events: Object.fromEntries(pairs) });
    })();
    return () => {
      cancelled = true;
    };
    // `idsKey` (not `animalIds`, a new array reference every render) is
    // the intentional, stable dependency this effect keys off of.
  }, [idsKey]);

  const isCurrent = resolved !== null && resolved.key === idsKey;
  const eventsByAnimal = isCurrent ? resolved.events : {};

  // A stable "now" for the life of this hook instance's resolved data,
  // not a fresh `Date.now()` per `hasRecentNote()` call — matches the
  // original Dashboard fan-out's "runs once on mount" framing.
  const nowMs = useMemo(() => Date.now(), []);

  function recentEvents(animalId: string): HealthEventOut[] {
    const events = eventsByAnimal[animalId];
    if (!events) return [];
    return events.filter(
      (e) =>
        (nowMs - new Date(`${e.event_date}T00:00:00`).getTime()) / DAY_MS <=
        HEALTH_NOTE_WINDOW_DAYS,
    );
  }

  function hasRecentNote(animalId: string): boolean {
    return recentEvents(animalId).length > 0;
  }

  function recentNoteLevel(animalId: string): HealthNoteLevel | null {
    return strongestLevel(recentEvents(animalId));
  }

  return { eventsByAnimal, loading: !isCurrent, hasRecentNote, recentNoteLevel };
}
